import React, { useState, useRef, useEffect, createRef } from 'react';
import {
  IonButton,
  IonFabButton,
  IonIcon,
  IonContent,
  IonPage,
  IonAlert,
  IonGrid,
  IonRow,
  IonCol,
  IonLoading,
  IonText,
  IonCheckbox,
  IonItem,
  IonLabel
} from '@ionic/react';
import './UploadPrescription.scss';
import {
  addOutline,
  checkmarkOutline,
  informationCircleOutline,
  trashOutline
} from 'ionicons/icons';
import { AppointmentStepProps } from '../../navigators/AppointmentNavigator';
import Stepper from '../../components/Stepper';
import AppHeader from '../../components/AppHeader';
import { Appointment } from '../../models';
import { AppointmentsRootState } from '../../store/reducers/appointments';
import { setAppointment } from '../../store/actions/appointments';
import { connect } from 'react-redux';
import FileRepository from '../../repositories/FileRepository';
import EventBus from '../../services/EventBus';
import FormError from '../../components/FormError';
import { AuthenticationRootState } from '../../store/reducers/authentication';

const mapStateToProps = (
  state: {
    appointments: AppointmentsRootState;
    authentication: AuthenticationRootState;
  },
  ownProps: any
) => {
  return {
    laboratory: state.appointments.appointment.laboratory,
    isLoggedIn: !!state.authentication.accessToken
  };
};

const mapDispatchToProps = (dispatch: Function, ownProps: any) => ({
  setAppointment: (appointment: Partial<Appointment>) =>
    dispatch(setAppointment(appointment))
});

type Props = AppointmentStepProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> & {
    isUploadMandatory: boolean;
    isCovid: boolean;
    titleLabel: string;
    subtitleLabel: string;
    continueButtonLabel: string;
  };

const UploadPrescription: React.FC<Props> = ({
  step,
  history,
  nextStepRouterLink = '/',
  setAppointment,
  isUploadMandatory = false,
  isCovid = false,
  laboratory,
  isLoggedIn,
  titleLabel = laboratory?.prescription_title
    ? laboratory?.prescription_title
    : 'Ajouter une ordonnance',
  subtitleLabel = laboratory?.prescription_description
    ? laboratory?.prescription_description
    : 'un fichier PDF ou une photo de votre ordonnance',
  continueButtonLabel = 'Continuer sans ordonnance',
  stepperComponent = <Stepper step={step} />
}) => {
  const [showReasonAlert, setShowReasonAlert] = useState(false);

  const [fileNames, setFileNames] = useState<string[]>([]);
  const [filesBlobUrl, setFilesBlobUrl] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [isAlertOpen, setAlertOpen] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [rgpdPrescription, setRgpdPrescription] = useState(false);
  const [error, setError] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [imgRefs, setImgRefs] = React.useState<
    React.MutableRefObject<HTMLImageElement>[]
  >([]);

  useEffect(() => {
    let tmpFilesBlobUrl: typeof filesBlobUrl = [];
    let tmpImgRefs: typeof imgRefs = [];

    if (files.length) {
      let newFiles: typeof filesBlobUrl = [];

      files.forEach((file) => {
        newFiles = [...newFiles, URL.createObjectURL(file)];
      });

      tmpFilesBlobUrl = newFiles;
    }

    files.forEach((file) => {
      tmpImgRefs = Array(tmpFilesBlobUrl.length)
        .fill(null)
        .map((_, i) => imgRefs[i] || createRef());

      tmpImgRefs.forEach((imgRef) => {
        if (imgRef.current) {
          imgRef.current.onload = () => {
            if (imgRef.current) {
              URL.revokeObjectURL(imgRef.current.src); // free memory
            }
          };
        }
      });
    });

    tmpImgRefs = Array(tmpFilesBlobUrl.length)
      .fill(null)
      .map((_, i) => imgRefs[i] || createRef());

    setFilesBlobUrl(tmpFilesBlobUrl);
    setImgRefs(tmpImgRefs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, setAppointment, error]);

  function processFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    // history.push(nextStepRouterLink);
    if (e.target.files && e.target.files[0]) {
      if (e.target.files[0].type === 'application/pdf') {
        const fileMatch = e.target.value.match(/[^\\/]*$/);

        if (fileMatch) {
          setFileNames([...fileNames, fileMatch[0]]);
        }
      } else if (['image/png', 'image/jpeg'].includes(e.target.files[0].type)) {
        setFileNames([...fileNames, '']);
      } else {
        EventBus.error('Type de fichier non supporté');
        return;
      }

      setFiles([...files, e.target.files[0]]);
    }

    e.target.value = '';
  }

  function removeFile(index: number) {
    setFileNames(fileNames.filter((_, i) => i !== index));
    setFiles(files.filter((_, i) => i !== index));
  }

  function onSubmitFiles() {
    setError(false);
    setSubmitLoading(true);
    FileRepository.upload(files)
      .then((files) => {
        setAppointment({ documents: files });
        history.push(nextStepRouterLink);
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  }

  function handleNeedRgpd() {
    if (isLoggedIn) {
      onSubmitFiles();
    } else {
      setError(true);
      if (rgpdPrescription) {
        onSubmitFiles();
      }
    }
  }

  function handleRgpdChecked(e: any) {
    setError(true);
    if (e.detail.checked) {
      setError(false);
    }
    setRgpdPrescription(e.detail.checked);
  }

  return (
    <IonPage>
      <AppHeader />
      {stepperComponent}
      <IonContent>
        <IonLoading isOpen={submitLoading} />
        <div className="wrapper wrapper--centered">
          {!isLoggedIn && (
            <>
              <IonItem lines="none" className="no-background">
                <IonCheckbox
                  name="test"
                  checked={rgpdPrescription}
                  onIonChange={(e) => handleRgpdChecked(e)}
                  className="ion-margin-end"
                />

                <IonLabel className="ion-text-wrap">
                  J'accepte de transmettre mon ordonnance* dans le cadre de ma
                  prise de rendez-vous Le Pass.
                </IonLabel>
              </IonItem>

              {error && (
                <FormError
                  message={`Merci d'accepter la transmission de votre ordonnance pour continuer`}
                />
              )}
            </>
          )}

          <div className="ion-padding ion-text-center">
            {laboratory?.prescription_message ? (
              <div className="booking-calendar__info booking-calendar__info--large ion-margin-bottom">
                <IonText>
                  <IonRow className="ion-align-items-center">
                    <IonCol size="auto">
                      <IonIcon size="large" icon={informationCircleOutline} />
                    </IonCol>
                    <IonCol className="prescription-body">
                      <IonRow>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: laboratory.prescription_message
                          }}
                        ></span>
                      </IonRow>
                      {laboratory?.prescription_popup && (
                        <>
                          <IonRow>
                            <IonButton
                              size="small"
                              onClick={() => setAlertOpen(true)}
                            >
                              En savoir plus
                            </IonButton>
                          </IonRow>
                        </>
                      )}
                    </IonCol>
                  </IonRow>
                </IonText>
                {laboratory?.prescription_popup && (
                  <IonAlert
                    cssClass={'prescription-popup'}
                    isOpen={isAlertOpen}
                    onDidDismiss={() => setAlertOpen(false)}
                    message={laboratory?.prescription_popup}
                    buttons={[
                      {
                        text: "D'accord",
                        handler: () => {}
                      }
                    ]}
                  />
                )}
              </div>
            ) : null}
            <IonGrid className="ion-margin-bottom">
              <IonRow>
                {filesBlobUrl.map((el, i) => (
                  <IonCol key={el} size={12 / imgRefs.length + ''}>
                    <IonButton
                      size="small"
                      shape="round"
                      className="prescription-image-delete"
                      onClick={() => removeFile(i)}
                    >
                      <IonIcon
                        slot="icon-only"
                        size="small"
                        icon={trashOutline}
                      />
                    </IonButton>
                    <img
                      ref={imgRefs[i]}
                      alt=""
                      src={el}
                      className="prescription-image ion-margin-bottom"
                    />
                    <div className="ion-margin-top">{fileNames[i]}</div>
                  </IonCol>
                ))}
              </IonRow>
            </IonGrid>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf, image/png, image/jpeg"
              id="prescriptionFile"
              className="prescription-file"
              onChange={(e) => processFileUpload(e)}
            />
            <div className="file-buttons">
              <label
                htmlFor="prescriptionFile"
                className="fab-add-prescription__label"
              >
                <IonFabButton class="fab-add-prescription" mode="md" is="div">
                  <IonIcon icon={addOutline} />
                </IonFabButton>
              </label>
              {files.length ? (
                <div className="ion-margin-start">
                  <IonFabButton
                    class="fab-add-prescription"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNeedRgpd();
                    }}
                    mode="md"
                    is="div"
                    data-cy="validatePrescription"
                  >
                    <IonIcon icon={checkmarkOutline} />
                  </IonFabButton>
                </div>
              ) : null}
            </div>
            <div>
              <h2>{titleLabel}</h2>
            </div>
            <p>{subtitleLabel}</p>
            {!isUploadMandatory && (
              <IonButton
                onClick={() => {
                  if (isCovid) {
                    setAppointment({ documents: undefined });
                    history.push(nextStepRouterLink);
                  } else {
                    setShowReasonAlert(true);
                  }
                }}
                fill={files.length ? 'clear' : 'solid'}
              >
                {continueButtonLabel}
              </IonButton>
            )}
            <IonAlert
              isOpen={showReasonAlert}
              onDidDismiss={() => setShowReasonAlert(false)}
              inputs={[
                {
                  name: 'reason',
                  type: 'textarea',
                  placeholder: 'Raison de votre visite',
                  attributes: {
                    required: true,
                    autoFocus: true,
                    autofocus: true,
                    minLength: 1
                  }
                }
              ]}
              header={'Veuillez nous expliquer la raison de votre visite.'}
              buttons={[
                {
                  text: 'Annuler',
                  role: 'cancel'
                },
                {
                  text: 'Valider',
                  handler: (values) => {
                    if (values.reason && values.reason.length >= 1) {
                      setAppointment({ reason: values.reason });
                      history.push(nextStepRouterLink);
                    } else {
                      return false;
                    }
                  }
                }
              ]}
            />
          </div>
          {!isLoggedIn && (
            <IonLabel className="ion-text-wrap ion-text-center ion-padding">
              <small>
                *votre ordonnance sera supprimé de notre serveur dans un délai
                de 1h si vous ne finalisez pas votre prise de rendez-vous sur
                notre service Le Pass.
              </small>
            </IonLabel>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(UploadPrescription);
